import React from 'react';
import { Box, Typography, Button, Chip, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const FREE_FEATURES = [
  'Full player stat leaders — all positions',
  'Nerd Ratings (NR) — positional player rankings',
  'Team advanced stats — EPA, success rate, explosive rate',
  'Team standings & schedules',
  'Individual team pages — roster, news, schedule',
];

const PREMIUM_FEATURES = [
  'EPA per play — every player, every skill position',
  'QB Situational Splits — shotgun, pressure, by down & quarter',
  'Drive-Level EPA Explorer — filter by field position zone',
  'Red Zone Tendencies — pass/run rates, TD%, goal-to-go',
  'Player card deep dives — CPOE, aDOT, explosive rate',
  'Success rate & pressure metrics at player level',
];

const COMING_SOON = [
  'Coverage shell tendencies per team (NGS)',
  'Fantasy projections using NR + snap share',
  'Pass-rush win rates by alignment',
  'Receiver route tree analysis',
];

function CheckIcon({ color = '#2ea84b' }) {
  return <Box sx={{ color, fontSize: '0.9rem', fontWeight: 900, lineHeight: 1 }}>✓</Box>;
}

function FeatureList({ items, color = '#2ea84b' }) {
  return (
    <List dense disablePadding>
      {items.map(f => (
        <ListItem key={f} disableGutters sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 22 }}><CheckIcon color={color} /></ListItemIcon>
          <ListItemText primary={f} primaryTypographyProps={{ sx: { fontSize: '0.8rem', color: '#c0d4e8', lineHeight: 1.4 } }} />
        </ListItem>
      ))}
    </List>
  );
}

/**
 * TierGate — wraps content behind a tier requirement with blur + overlay.
 *
 * Props:
 *   tier        — 'free' (requires sign in) | 'premium' (requires Nerd+)
 *   feature     — short label for the locked feature
 *   compact     — smaller overlay (for cards)
 *   children    — the content to render when unlocked
 */
export default function TierGate({ children, tier = 'premium', feature, compact = false }) {
  const { isBasic, isPremium, isAdmin, authLoading, setAuthModalOpen, setUpgradeModalOpen } = useAuth();

  if (authLoading) return null;

  // isBasic/isPremium/isAdmin are all sim-aware — they reflect viewAsTier when admin is simulating
  const passes =
    tier === 'free'    ? (!isBasic || isAdmin) :   // passes for free, premium, and admin
    tier === 'premium' ? (isPremium || isAdmin) :  // passes for premium and admin (real or simulated)
    true;

  if (passes) return <>{children}</>;

  const isFreeGate = tier === 'free';

  const handleCTA = () => {
    if (isFreeGate) setAuthModalOpen(true);
    else setUpgradeModalOpen(true);
  };

  if (compact) {
    return (
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.35 }}>
          {children}
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(7,18,32,0.85)', backdropFilter: 'blur(2px)',
          borderRadius: 2, border: `1px solid ${isFreeGate ? '#2ea84b44' : '#f5a62344'}`,
          p: 2, textAlign: 'center', gap: 1,
        }}>
          <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{isFreeGate ? '🔓' : '⚡'}</Typography>
          <Chip
            label={isFreeGate ? 'Free Account' : 'Nerds+'}
            size="small"
            sx={{
              fontWeight: 800, fontSize: '0.65rem',
              bgcolor: isFreeGate ? '#0a2a14' : '#2a1a05',
              color: isFreeGate ? '#2ea84b' : '#f5a623',
              border: `1px solid ${isFreeGate ? '#2ea84b66' : '#f5a62366'}`,
            }}
          />
          {feature && <Typography variant="caption" sx={{ color: '#9eb8d0', fontWeight: 600 }}>{feature}</Typography>}
          <Button size="small" variant="contained"
            color={isFreeGate ? 'primary' : 'secondary'}
            onClick={handleCTA}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}>
            {isFreeGate ? 'Sign Up Free' : 'Unlock Nerds+'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      {/* Blurred preview — stays in page flow */}
      <Box sx={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.3, minHeight: 200 }}>
        {children}
      </Box>

      {/* Tint overlay covering the blurred area */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(5,12,24,0.6)', borderRadius: 2 }} />

      {/* CTA panel — fixed to viewport center so it's always visible regardless of scroll */}
      <Box sx={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1250,
        width: { xs: 'calc(100vw - 32px)', sm: 680 },
        maxWidth: 720,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
      }}>
        <Box sx={{
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 3, sm: 4 },
          backgroundColor: 'rgba(10,22,38,0.99)', borderRadius: 3,
          border: `1px solid ${isFreeGate ? '#2ea84b55' : '#f5a62355'}`,
          p: { xs: 3, sm: 4 }, boxShadow: isFreeGate
            ? '0 8px 80px rgba(46,168,75,0.25), 0 0 0 1px rgba(46,168,75,0.15)'
            : '0 8px 80px rgba(245,166,35,0.25), 0 0 0 1px rgba(245,166,35,0.15)',
        }}>

          {/* Left — what you get */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>{isFreeGate ? '🔓' : '⚡'}</Typography>
              <Box>
                <Chip
                  label={isFreeGate ? 'Free Account' : 'Nerds+ — $6/mo'}
                  size="small"
                  sx={{
                    fontWeight: 800, fontSize: '0.68rem', mb: 0.5,
                    bgcolor: isFreeGate ? '#0a2a14' : '#2a1a05',
                    color: isFreeGate ? '#2ea84b' : '#f5a623',
                    border: `1px solid ${isFreeGate ? '#2ea84b66' : '#f5a62366'}`,
                  }}
                />
              </Box>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, color: '#e8f0f8', lineHeight: 1.2 }}>
              {isFreeGate
                ? `${feature || 'This feature'} requires a free account`
                : `${feature || 'Advanced Analytics'} — Nerds+ only`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7a9ab8', mb: 2 }}>
              {isFreeGate
                ? 'Create a free account to unlock full player stats, Nerd Ratings, and team analytics.'
                : 'Upgrade to Nerds+ for EPA data, situational splits, and deep-dive analytics.'}
            </Typography>

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: isFreeGate ? '#2ea84b' : '#f5a623', mb: 1, letterSpacing: '0.06em' }}>
              {isFreeGate ? 'FREE ACCOUNT INCLUDES:' : 'NERDS+ INCLUDES:'}
            </Typography>
            <FeatureList items={isFreeGate ? FREE_FEATURES : PREMIUM_FEATURES} color={isFreeGate ? '#2ea84b' : '#f5a623'} />

            {/* CTA Buttons */}
            <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button variant="contained" color={isFreeGate ? 'primary' : 'secondary'}
                onClick={handleCTA} size="large"
                sx={{
                  textTransform: 'none', fontWeight: 800, px: 3,
                  boxShadow: isFreeGate ? '0 4px 20px rgba(46,168,75,0.35)' : '0 4px 20px rgba(245,166,35,0.35)',
                }}>
                {isFreeGate ? 'Sign Up Free →' : 'Upgrade to Nerds+ →'}
              </Button>
              {isFreeGate && (
                <Button variant="outlined" color="primary"
                  onClick={() => setAuthModalOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Sign In
                </Button>
              )}
            </Box>
          </Box>

          {/* Right — coming soon / next tier teaser */}
          <Box sx={{
            width: { xs: '100%', sm: 200 },
            borderLeft: { xs: 'none', sm: `1px solid ${isFreeGate ? '#2ea84b22' : '#f5a62322'}` },
            borderTop: { xs: `1px solid ${isFreeGate ? '#2ea84b22' : '#f5a62322'}`, sm: 'none' },
            pl: { xs: 0, sm: 3 }, pt: { xs: 2, sm: 0 },
          }}>
            {isFreeGate ? (
              <>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#f5a623', mb: 1, letterSpacing: '0.06em' }}>
                  ⚡ NERDS+ ALSO ADDS:
                </Typography>
                <FeatureList items={PREMIUM_FEATURES.slice(0, 4)} color='#f5a623' />
                <Typography sx={{ fontSize: '0.68rem', color: '#4a6080', mt: 1.5, fontStyle: 'italic' }}>
                  $6/mo after free tier
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#9eb8d0', mb: 1, letterSpacing: '0.06em' }}>
                  🔬 COMING TO NERDS+:
                </Typography>
                <FeatureList items={COMING_SOON} color='rgba(245,166,35,0.4)' />
              </>
            )}
          </Box>

        </Box>
      </Box>  {/* end CTA panel */}
    </Box>
  );
}
